import type { RouteLocationRaw, RouteLocationNormalized, NavigationGuardNext } from 'vue-router';

import { RedirectRule } from "@/lib/tellscale/router-guard/redirect-rule.ts";

/**
 * Router guard manager for declarative redirect rule definition
 *
 * Provides a chainable API for defining complex redirect logic
 * in a clean, testable, and maintainable way.
 */
export class RouterGuard {
    private rules: RedirectRule[] = [];

    private to: RouteLocationNormalized;

    // @ts-ignore
    private from: RouteLocationNormalized;

    // @ts-ignore
    private next: NavigationGuardNext;

    /**
     * Creates a new router guard instance
     *
     * @param to - Target route location
     * @param from - Source route location
     * @param next - Vue Router next function
     */
    constructor(to: RouteLocationNormalized, from: RouteLocationNormalized, next: NavigationGuardNext) {
        this.to = to;
        this.from = from;
        this.next = next;
    }

    /**
     * Adds a new redirect rule and returns it for chaining
     *
     * @param redirect - The route location to redirect to
     * @returns RedirectRule instance for chaining conditions
     *
     * @example
     * guard.addRedirectRule({ path: '/login', query: to.query })
     *     .checkCondition(() => !auth.isAuthenticated)
     *     .checkMeta('auth', 'authenticated', true);
     */
    public addRedirectRule = (redirect: RouteLocationRaw): RedirectRule => {
        const rule = new RedirectRule(redirect, this.to.path);

        this.rules.push(rule);

        return rule;
    }

    /**
     * Validates all rules and returns the first matching redirect
     *
     * @returns The redirect location if a rule matches, null otherwise
     *
     * @example
     * const redirect = guard.validate();
     * if (redirect) return next(redirect);
     */
    public validate = (): RouteLocationRaw | null => {
        for(const rule of this.rules) {
            if(rule.shouldRedirect(this.to.meta)) {
                return rule.getRedirect();
            }
        }

        return null;
    }
}